import { useQuery } from '@tanstack/react-query';
import { Outlet, useMatchRoute, useParams } from '@tanstack/react-router';
import type { ProjectId } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { projectsRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import {
  AppBar,
  AppIcons,
  type AppIconName,
  BackButton,
  Breadcrumb,
  type BreadcrumbItem,
  TabBar,
  useEscapeKey,
  useGoBack,
  useOrgContext,
} from '@web/ui/core';

type ActiveTab = 'identity' | 'squads' | 'users' | 'settings';

function editorCopy(
  mode: 'create' | 'edit',
  projectName?: string,
): { title: string; eyebrow: string } {
  return mode === 'edit'
    ? {
        title: projectName ?? t('project.editor.titleFallback'),
        eyebrow: t('project.editor.eyebrowEdit'),
      }
    : { title: t('project.editor.titleCreate'), eyebrow: t('project.editor.eyebrowCreate') };
}

function editorReady(mode: 'create' | 'edit', isLoading: boolean, hasProject: boolean): boolean {
  return mode === 'create' || (!isLoading && hasProject);
}

function resolveActiveTab(isSquads: boolean, isUsers: boolean, isSettings: boolean): ActiveTab {
  if (isSquads) return 'squads';
  if (isUsers) return 'users';
  if (isSettings) return 'settings';
  return 'identity';
}

interface ProjectEditorTabsProps {
  readonly orgSlug: string;
  readonly projectId: ProjectId | undefined;
  readonly activeTab: ActiveTab;
  readonly tabsLocked: boolean;
  readonly showUsersTab: boolean;
  readonly showSettingsTab: boolean;
  readonly squadCount: number;
}

function ProjectEditorTabs({
  orgSlug,
  projectId,
  activeTab,
  tabsLocked,
  showUsersTab,
  showSettingsTab,
  squadCount,
}: ProjectEditorTabsProps): React.JSX.Element {
  const hintLocked = t('project.editor.tabs.hintLockedCreate');

  function identityTab(): React.JSX.Element {
    if (tabsLocked) {
      return (
        <TabBar.Tab
          icon="identity"
          label={t('project.editor.tabs.identity')}
          isActive={activeTab === 'identity'}
          disabled
          hint={hintLocked}
        />
      );
    }
    return (
      <TabBar.Tab
        to="/orgs/$orgSlug/project/$projectId"
        params={{ orgSlug, projectId: projectId as ProjectId }}
        icon="identity"
        label={t('project.editor.tabs.identity')}
        isActive={activeTab === 'identity'}
      />
    );
  }

  function squadsTab(): React.JSX.Element {
    if (tabsLocked) {
      return (
        <TabBar.Tab
          icon="users"
          label={t('project.editor.tabs.squads')}
          count={squadCount}
          isActive={activeTab === 'squads'}
          disabled
          hint={hintLocked}
        />
      );
    }
    return (
      <TabBar.Tab
        to="/orgs/$orgSlug/project/$projectId/squads"
        params={{ orgSlug, projectId: projectId as ProjectId }}
        icon="users"
        label={t('project.editor.tabs.squads')}
        count={squadCount}
        isActive={activeTab === 'squads'}
      />
    );
  }

  function usersTab(): React.JSX.Element | null {
    if (!showUsersTab) return null;
    if (tabsLocked) {
      return (
        <TabBar.Tab
          icon="user"
          label={t('project.editor.tabs.users')}
          isActive={activeTab === 'users'}
          disabled
          hint={hintLocked}
        />
      );
    }
    return (
      <TabBar.Tab
        to="/orgs/$orgSlug/project/$projectId/users"
        params={{ orgSlug, projectId: projectId as ProjectId }}
        icon="user"
        label={t('project.editor.tabs.users')}
        isActive={activeTab === 'users'}
      />
    );
  }

  function settingsTab(): React.JSX.Element | null {
    if (!showSettingsTab) return null;
    if (tabsLocked) {
      return (
        <TabBar.Tab
          icon="settings"
          label={t('project.editor.tabs.settings')}
          isActive={activeTab === 'settings'}
          disabled
          hint={hintLocked}
        />
      );
    }
    return (
      <TabBar.Tab
        to="/orgs/$orgSlug/project/$projectId/settings"
        params={{ orgSlug, projectId: projectId as ProjectId }}
        icon="settings"
        label={t('project.editor.tabs.settings')}
        isActive={activeTab === 'settings'}
      />
    );
  }

  return (
    <TabBar ariaLabel={t('project.editor.tabsAria')}>
      {identityTab()}
      {squadsTab()}
      {usersTab()}
      {settingsTab()}
    </TabBar>
  );
}

export function ProjectEditorLayout(): React.JSX.Element | null {
  const { projectId } = useParams({ strict: false });
  const matchRoute = useMatchRoute();
  const { orgId, orgSlug } = useOrgContext();

  const mode: 'create' | 'edit' = projectId ? 'edit' : 'create';

  const { data: project, isLoading } = useQuery({
    queryKey: queryKeys.projects.detail(orgId, projectId as ProjectId),
    queryFn: () => projectsRepository.getProject(orgId, projectId as ProjectId),
    enabled: !!projectId,
  });

  const goBack = useGoBack({ to: '/orgs/$orgSlug/overview', params: { orgSlug } });

  useEscapeKey(goBack);

  const isReady = editorReady(mode, isLoading, Boolean(project));
  if (!isReady) return null;

  const { title: titleText, eyebrow } = editorCopy(mode, project?.name);
  const heroIcon: AppIconName = (project?.icon as AppIconName | undefined) ?? 'headset';

  const isSquads = !!matchRoute({ to: '/orgs/$orgSlug/project/$projectId/squads' });
  const isUsers = !!matchRoute({ to: '/orgs/$orgSlug/project/$projectId/users' });
  const isSettings = !!matchRoute({ to: '/orgs/$orgSlug/project/$projectId/settings' });
  const activeTab = resolveActiveTab(isSquads, isUsers, isSettings);

  const squadCount = project?.squads.length ?? 0;
  const tabsLocked = mode === 'create';
  // In edit mode, hide tabs whose actions the caller cannot perform.
  // Backend still 403s the underlying calls — these gates are UX. Route
  // guards block direct navigation; tabs just stop advertising disabled
  // capabilities.
  const projectPerms = project?.permissions ?? [];
  const canManageMembers = projectPerms.includes('kanban:project-members:read');
  const canUpdateProject = projectPerms.includes('kanban:projects:update');
  const showUsersTab = tabsLocked || canManageMembers;
  const showSettingsTab = tabsLocked || canUpdateProject;

  const crumbs: BreadcrumbItem[] = [
    { label: t('home.nav.process') },
    { label: t('home.crumb.projects') },
    ...(mode === 'edit' && project ? [{ label: project.name }] : []),
    { label: eyebrow },
  ];

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-paper outline-none">
      <AppBar
        leading={
          <div className="inline-flex items-center gap-3.5">
            <BackButton label={t('project.editor.actions.back')} onClick={goBack} />
            <Breadcrumb items={crumbs} />
          </div>
        }
      />

      <section className="border-b border-border-soft px-7 py-3.5">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary-tint text-primary-ink">
            <AppIcons name={heroIcon} size={16} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
              {eyebrow}
            </span>
            <h1 className="m-0 truncate font-serif text-h-display font-normal leading-display tracking-display">
              {titleText}
            </h1>
          </div>
        </div>
      </section>

      <ProjectEditorTabs
        orgSlug={orgSlug}
        projectId={projectId as ProjectId | undefined}
        activeTab={activeTab}
        tabsLocked={tabsLocked}
        showUsersTab={showUsersTab}
        showSettingsTab={showSettingsTab}
        squadCount={squadCount}
      />

      <Outlet />
    </div>
  );
}
